import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  ChevronRight,
  Check,
  RotateCcw,
  Clock,
  Shield,
  DollarSign,
  Smartphone,
  RefreshCw,
  Calendar,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAppSelector } from '@/store/hooks';
import {
  getResolvedSettings,
  updatePolicyGroup,
} from '@/features/settings-policies/api';
import {
  type PolicyGroupKey,
  type ResolvedSettings,
} from '@/features/settings-policies/settings-policies';

export const Route = createFileRoute('/_layout/settings/policies')({
  component: PoliciesPage,
});

/* ── Display labels for backend enums ─────────────────────── */

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};
const OVERPAYMENT_LABELS: Record<string, string> = {
  applyToFuture: 'Apply to future installments',
  wallet: 'Credit wallet',
  refund: 'Refund',
};
const APPROVAL_LABELS: Record<string, string> = {
  branchManager: 'Branch Manager',
  tenantAdmin: 'Tenant Admin',
};

const GROUP_KEYS: readonly PolicyGroupKey[] = [
  'payments',
  'retryEscalation',
  'deviceLock',
  'reconciliation',
  'approvals',
] as const;

/* ── Presentational row (mirrors the original layout) ─────── */

interface PolicyRowProps {
  icon: React.ReactNode;
  name: string;
  purpose: string;
  hint: string;
  owner: string;
  children: React.ReactNode;
  showDivider: boolean;
}

function PolicyRow({
  icon,
  name,
  purpose,
  hint,
  owner,
  children,
  showDivider,
}: Readonly<PolicyRowProps>) {
  return (
    <div>
      {showDivider && <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="p-1.5 rounded-lg bg-muted/50 text-muted-foreground shrink-0 mt-0.5">
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{purpose}</p>
            <p className="text-[10px] text-muted-foreground/70 mt-1">
              Default: {hint}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:min-w-[220px]">
          <div className="flex-1">{children}</div>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {owner}
          </Badge>
        </div>
      </div>
    </div>
  );
}

/* ── Group card wrapper ───────────────────────────────────── */

interface GroupCardProps {
  title: string;
  description: string;
  color: string;
  children: React.ReactNode;
}

function GroupCard({
  title,
  description,
  color,
  children,
}: Readonly<GroupCardProps>) {
  return (
    <Card className="shadow-md border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-xl bg-gradient-to-br ${color} text-white shadow-lg`}
          >
            <Settings className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-0">{children}</CardContent>
    </Card>
  );
}

/* ── Small typed controls ─────────────────────────────────── */

function NumberControl({
  value,
  onChange,
  min,
  max,
  unit,
  disabled,
}: Readonly<{
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  unit?: string;
  disabled?: boolean;
}>) {
  return (
    <div className="flex items-center gap-2 justify-end">
      <Input
        type="number"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : ''}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isNaN(n)) return;
          onChange(Math.min(max, Math.max(min, n)));
        }}
        className="w-24 h-9 text-sm text-center"
      />
      {unit && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {unit}
        </span>
      )}
    </div>
  );
}

function ToggleControl({
  checked,
  onChange,
  onLabel,
  offLabel,
  disabled,
}: Readonly<{
  checked: boolean;
  onChange: (v: boolean) => void;
  onLabel: string;
  offLabel: string;
  disabled?: boolean;
}>) {
  return (
    <div className="flex items-center gap-2 justify-end">
      <span className="text-xs text-muted-foreground">
        {checked ? onLabel : offLabel}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────── */

function PoliciesPage() {
  const { toast } = useToast();
  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const selectedTenantId = useAppSelector((s) => s.tenant.selectedTenantId);
  const tenantId = selectedTenantId ?? user?.tenantId ?? null;
  const canUpdate = hasPermission('settings', 'update');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['settings-policies', tenantId],
    queryFn: () => getResolvedSettings(tenantId as string),
    enabled: !!tenantId,
  });

  // Working copy, re-seeded whenever server data changes (tenant switch / refetch).
  const [draft, setDraft] = useState<ResolvedSettings | null>(null);
  useEffect(() => {
    if (data) setDraft(structuredClone(data));
  }, [data]);

  const isDirty = (g: PolicyGroupKey): boolean =>
    !!draft && !!data && JSON.stringify(draft[g]) !== JSON.stringify(data[g]);
  const hasChanges = !!draft && GROUP_KEYS.some(isDirty);

  const mutation = useMutation({
    mutationFn: async (): Promise<PolicyGroupKey[]> => {
      if (!draft || !tenantId) return [];
      const saved: PolicyGroupKey[] = [];
      const tasks: Promise<unknown>[] = [];
      // Literal-keyed calls keep each payload type-checked against its group.
      if (isDirty('payments')) {
        tasks.push(updatePolicyGroup(tenantId, 'payments', draft.payments));
        saved.push('payments');
      }
      if (isDirty('retryEscalation')) {
        tasks.push(
          updatePolicyGroup(tenantId, 'retryEscalation', draft.retryEscalation)
        );
        saved.push('retryEscalation');
      }
      if (isDirty('deviceLock')) {
        tasks.push(updatePolicyGroup(tenantId, 'deviceLock', draft.deviceLock));
        saved.push('deviceLock');
      }
      if (isDirty('reconciliation')) {
        tasks.push(
          updatePolicyGroup(tenantId, 'reconciliation', draft.reconciliation)
        );
        saved.push('reconciliation');
      }
      if (isDirty('approvals')) {
        tasks.push(updatePolicyGroup(tenantId, 'approvals', draft.approvals));
        saved.push('approvals');
      }
      await Promise.all(tasks);
      return saved;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({
        queryKey: ['settings-policies', tenantId],
      });
      toast({
        title: 'Policies saved',
        description: `Updated ${saved.length} policy group${saved.length === 1 ? '' : 's'}.`,
      });
    },
    onError: () => {
      toast({
        title: 'Could not save policies',
        description: 'The policy update was rejected. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleReset = () => {
    if (data) setDraft(structuredClone(data));
  };

  /* ── Render guards ───────────────────────────────────────── */

  const breadcrumb = (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Link to="/settings" className="hover:text-foreground transition-colors">
        Settings
      </Link>
      <ChevronRight className="w-3.5 h-3.5" />
      <span className="text-foreground font-medium">Policy Rules</span>
    </div>
  );

  if (!tenantId) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <Card className="border-0 shadow-md">
          <CardContent className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <Info className="h-4 w-4" />
            Select a tenant to configure its policy rules.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || !draft) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading policy rules…</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <Card className="border-0 shadow-md">
          <CardContent className="flex flex-col items-start gap-3 py-8">
            <p className="text-sm text-muted-foreground">
              Couldn’t load policy rules for this tenant.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main form ───────────────────────────────────────────── */

  return (
    <div className="space-y-6">
      {breadcrumb}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Configurable Policy Rules
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Rules that should be configurable rather than hardcoded — managed by
            Tenant Admin
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && !mutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {hasChanges && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleReset}
                disabled={mutation.isPending}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => mutation.mutate()}
                disabled={!canUpdate || mutation.isPending}
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      {!canUpdate && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          You have read-only access — saving requires the “settings” update
          permission.
        </div>
      )}

      {/* Payments */}
      <GroupCard
        title="Payments"
        description="Deduction cadence, timing and overpayment handling"
        color="from-teal-500 to-teal-600"
      >
        <PolicyRow
          icon={<Calendar className="w-4 h-4" />}
          name="Deduction Frequency"
          purpose="Defines repayment cadence."
          hint="Monthly"
          owner="Tenant Admin"
          showDivider={false}
        >
          <Select
            value={draft.payments.deductionFrequency}
            onValueChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      payments: {
                        ...d.payments,
                        deductionFrequency:
                          v as ResolvedSettings['payments']['deductionFrequency'],
                      },
                    }
                  : d
              )
            }
          >
            <SelectTrigger className="w-full h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['daily', 'weekly', 'monthly'] as const).map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {FREQUENCY_LABELS[opt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PolicyRow>

        <PolicyRow
          icon={<Clock className="w-4 h-4" />}
          name="Deduction Time Window"
          purpose="Salary-aligned window improves wallet-funded success."
          hint="18:00-21:00"
          owner="Tenant Admin"
          showDivider
        >
          <Input
            value={draft.payments.deductionTimeWindow}
            placeholder="18:00-21:00"
            maxLength={20}
            onChange={(e) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      payments: {
                        ...d.payments,
                        deductionTimeWindow: e.target.value,
                      },
                    }
                  : d
              )
            }
            className="h-9 text-sm"
          />
        </PolicyRow>

        <PolicyRow
          icon={<DollarSign className="w-4 h-4" />}
          name="Minimum Payment Threshold"
          purpose="Whether a partial payment keeps the device active."
          hint="50% of installment"
          owner="Tenant Admin"
          showDivider
        >
          <div className="flex items-center gap-2 justify-end">
            <Select
              value={draft.payments.minimumPaymentThreshold.type}
              onValueChange={(v) =>
                setDraft((d) =>
                  d
                    ? {
                        ...d,
                        payments: {
                          ...d.payments,
                          minimumPaymentThreshold: {
                            ...d.payments.minimumPaymentThreshold,
                            type: v as 'pct' | 'amount',
                          },
                        },
                      }
                    : d
                )
              }
            >
              <SelectTrigger className="w-24 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pct">%</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={0}
              value={draft.payments.minimumPaymentThreshold.value}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isNaN(n)) return;
                setDraft((d) =>
                  d
                    ? {
                        ...d,
                        payments: {
                          ...d.payments,
                          minimumPaymentThreshold: {
                            ...d.payments.minimumPaymentThreshold,
                            value: Math.max(0, n),
                          },
                        },
                      }
                    : d
                );
              }}
              className="w-20 h-9 text-sm text-center"
            />
          </div>
        </PolicyRow>

        <PolicyRow
          icon={<Wallet className="w-4 h-4" />}
          name="Overpayment Handling"
          purpose="Treatment of excess payments."
          hint="Apply to future installments"
          owner="Finance / Tenant Admin"
          showDivider
        >
          <Select
            value={draft.payments.overpaymentHandling}
            onValueChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      payments: {
                        ...d.payments,
                        overpaymentHandling:
                          v as ResolvedSettings['payments']['overpaymentHandling'],
                      },
                    }
                  : d
              )
            }
          >
            <SelectTrigger className="w-full h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['applyToFuture', 'wallet', 'refund'] as const).map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {OVERPAYMENT_LABELS[opt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PolicyRow>
      </GroupCard>

      {/* Retry & Escalation */}
      <GroupCard
        title="Retry & Escalation"
        description="Failed-payment retry and escalation rules"
        color="from-amber-500 to-amber-600"
      >
        <PolicyRow
          icon={<RefreshCw className="w-4 h-4" />}
          name="Retry Count"
          purpose="Failed attempts before escalation."
          hint="3 retries"
          owner="Tenant Admin"
          showDivider={false}
        >
          <NumberControl
            value={draft.retryEscalation.retryCount}
            min={1}
            max={10}
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      retryEscalation: { ...d.retryEscalation, retryCount: v },
                    }
                  : d
              )
            }
          />
        </PolicyRow>

        <PolicyRow
          icon={<Clock className="w-4 h-4" />}
          name="Retry Interval"
          purpose="Time between retry attempts."
          hint="24 hours"
          owner="Tenant Admin"
          showDivider
        >
          <NumberControl
            value={draft.retryEscalation.retryIntervalHours}
            min={1}
            max={720}
            unit="hours"
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      retryEscalation: {
                        ...d.retryEscalation,
                        retryIntervalHours: v,
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>

        <PolicyRow
          icon={<Shield className="w-4 h-4" />}
          name="Grace Period"
          purpose="Days before lock after a failed deduction."
          hint="7 days"
          owner="Tenant Admin"
          showDivider
        >
          <NumberControl
            value={draft.retryEscalation.gracePeriodDays}
            min={0}
            max={90}
            unit="days"
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      retryEscalation: {
                        ...d.retryEscalation,
                        gracePeriodDays: v,
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>
      </GroupCard>

      {/* Device Lock */}
      <GroupCard
        title="Device Lock"
        description="When devices are restricted and restored"
        color="from-violet-500 to-violet-600"
      >
        <PolicyRow
          icon={<Smartphone className="w-4 h-4" />}
          name="Lock After Grace Period"
          purpose="Only restrict once the grace period has elapsed."
          hint="Enabled"
          owner="Tenant Admin"
          showDivider={false}
        >
          <ToggleControl
            checked={draft.deviceLock.lockThreshold.afterGrace}
            onLabel="After grace"
            offLabel="Immediate"
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      deviceLock: {
                        ...d.deviceLock,
                        lockThreshold: {
                          ...d.deviceLock.lockThreshold,
                          afterGrace: v,
                        },
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>

        <PolicyRow
          icon={<DollarSign className="w-4 h-4" />}
          name="Lock Overdue Amount"
          purpose="Overdue amount that triggers a restriction."
          hint="0"
          owner="Tenant Admin"
          showDivider
        >
          <NumberControl
            value={draft.deviceLock.lockThreshold.overdueAmount}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      deviceLock: {
                        ...d.deviceLock,
                        lockThreshold: {
                          ...d.deviceLock.lockThreshold,
                          overdueAmount: v,
                        },
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>

        <PolicyRow
          icon={<CheckCircle2 className="w-4 h-4" />}
          name="Restore On Overdue Cleared"
          purpose="Restore the device once overdue balance is cleared."
          hint="Enabled"
          owner="Tenant Admin"
          showDivider
        >
          <ToggleControl
            checked={draft.deviceLock.unlockThreshold.clearOverdue}
            onLabel="On clear"
            offLabel="Manual"
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      deviceLock: {
                        ...d.deviceLock,
                        unlockThreshold: {
                          ...d.deviceLock.unlockThreshold,
                          clearOverdue: v,
                        },
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>

        <PolicyRow
          icon={<DollarSign className="w-4 h-4" />}
          name="Minimum Restore Amount"
          purpose="Minimum payment that restores a restricted device."
          hint="0"
          owner="Tenant Admin"
          showDivider
        >
          <NumberControl
            value={draft.deviceLock.unlockThreshold.minRestoreAmount}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            onChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      deviceLock: {
                        ...d.deviceLock,
                        unlockThreshold: {
                          ...d.deviceLock.unlockThreshold,
                          minRestoreAmount: v,
                        },
                      },
                    }
                  : d
              )
            }
          />
        </PolicyRow>
      </GroupCard>

      {/* Reconciliation */}
      <GroupCard
        title="Reconciliation"
        description="Offline payment sync expectations"
        color="from-blue-500 to-blue-600"
      >
        <PolicyRow
          icon={<Clock className="w-4 h-4" />}
          name="Offline Sync SLA"
          purpose="How long agents have to sync offline payments."
          hint="24 hours"
          owner="Branch Manager / Tenant Admin"
          showDivider={false}
        >
          <NumberControl
            value={draft.reconciliation.offlineSyncSlaHours}
            min={1}
            max={168}
            unit="hours"
            onChange={(v) =>
              setDraft((d) =>
                d ? { ...d, reconciliation: { offlineSyncSlaHours: v } } : d
              )
            }
          />
        </PolicyRow>
      </GroupCard>

      {/* Approvals */}
      <GroupCard
        title="Approvals"
        description="Who can adjust records and write off losses"
        color="from-red-500 to-red-600"
      >
        <PolicyRow
          icon={<AlertTriangle className="w-4 h-4" />}
          name="Manual Adjustment Approval"
          purpose="Who can correct payment records."
          hint="Branch Manager"
          owner="Tenant Admin"
          showDivider={false}
        >
          <Select
            value={draft.approvals.manualAdjustmentApproval}
            onValueChange={(v) =>
              setDraft((d) =>
                d
                  ? {
                      ...d,
                      approvals: {
                        ...d.approvals,
                        manualAdjustmentApproval:
                          v as ResolvedSettings['approvals']['manualAdjustmentApproval'],
                      },
                    }
                  : d
              )
            }
          >
            <SelectTrigger className="w-full h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['branchManager', 'tenantAdmin'] as const).map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {APPROVAL_LABELS[opt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PolicyRow>

        <PolicyRow
          icon={<Shield className="w-4 h-4" />}
          name="Write-off Approval"
          purpose="Fixed to Tenant Admin — not delegable to a lower role."
          hint="Tenant Admin only"
          owner="Tenant Admin"
          showDivider
        >
          <Input
            value="Tenant Admin"
            disabled
            readOnly
            className="h-9 text-sm bg-muted/40"
          />
        </PolicyRow>
      </GroupCard>
    </div>
  );
}
